import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Link,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import getApiBase from "./getApiBase";

const getInvoiceLogoUrl = () => `${window.location.origin}/UpdatedMainLogo.png`;

const formatShortDate = (dateString) => {
  if (!dateString || dateString === "—") return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatMonthLabel = (monthKey) => {
  if (!monthKey) return "";
  const [year, month] = monthKey.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

const formatShopAddress = (shop) => {
  if (!shop) return "";
  const parts = [];
  if (shop.street_address || shop.address) parts.push(shop.street_address || shop.address);
  if (shop.city) parts.push(shop.city);
  if (shop.state) parts.push(shop.state);
  if (shop.zip_code || shop.zip) parts.push(shop.zip_code || shop.zip);
  return parts.join(", ");
};

const formatShopLabel = (shop) => {
  const name = shop.shop_name || shop.name || "Shop";
  const addr = formatShopAddress(shop);
  return addr ? `${name} — ${addr}` : name;
};

const formatCell = (col, value) => {
  if (col.id === "amount" || col.id === "subtotal_pre_tax") {
    return `$${Number(value || 0).toFixed(2)}`;
  }
  if (col.id === "rate_per_minute") {
    return `$${Number(value || 0).toFixed(4)}`;
  }
  if (col.id === "duration_minutes") {
    return Number(value || 0).toFixed(2);
  }
  if (col.id === "completed_at" || col.id === "period_start" || col.id === "call_started_at") {
    return formatShortDate(value);
  }
  return value ?? "—";
};

const InvoiceLogoHeader = ({ shop, invoice, pageLabel }) => (
  <div
    className="invoice-header"
    style={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
      marginBottom: 20,
      paddingBottom: 16,
      borderBottom: "2px solid #e63926",
    }}
  >
    <div>
      <img
        src={getInvoiceLogoUrl()}
        alt="Autoshops.com"
        className="invoice-logo"
        style={{ maxHeight: 52, width: "auto", display: "block" }}
      />
    </div>
    <div style={{ textAlign: "right", maxWidth: "55%" }}>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Monthly Statement</div>
      {shop?.shop_name && (
        <div style={{ fontSize: 13, color: "#333", fontWeight: 600 }}>{shop.shop_name}</div>
      )}
      {shop?.account_number && (
        <div style={{ fontSize: 12, color: "#555" }}>Account #{shop.account_number}</div>
      )}
      {shop?.address_line && (
        <div style={{ fontSize: 12, color: "#555" }}>{shop.address_line}</div>
      )}
      {invoice?.invoice_id && (
        <div style={{ fontSize: 11, color: "#777", marginTop: 4 }}>{invoice.invoice_id}</div>
      )}
      {pageLabel && (
        <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>{pageLabel}</div>
      )}
    </div>
  </div>
);

const ShopMonthlyStatement = ({ mechanicId, shops = [], initialShopId = null }) => {
  const activeShops = useMemo(
    () => shops.filter((s) => s.status === "Active" && s.in_network !== false),
    [shops],
  );

  const [billingShopId, setBillingShopId] = useState(initialShopId || activeShops[0]?.id || null);
  const [open, setOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [statements, setStatements] = useState([]);
  const [upcomingStatement, setUpcomingStatement] = useState(null);
  const [currentMonth, setCurrentMonth] = useState("");
  const [hasAiVoice, setHasAiVoice] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialShopId) setBillingShopId(initialShopId);
    else if (activeShops.length === 1) setBillingShopId(activeShops[0].id);
  }, [initialShopId, activeShops]);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const isAccruingMonth = useCallback(
    (monthKey) => Boolean(upcomingStatement?.statement_month && monthKey === upcomingStatement.statement_month),
    [upcomingStatement],
  );

  const fetchStatements = useCallback(async () => {
    if (!billingShopId) return;
    try {
      setLoading(true);
      setError(null);
      const apiBase = getApiBase();
      const res = await fetch(
        `${apiBase}/api/mechanicProfile/monthly-statements?mechanic_id=${mechanicId}&shop_id=${billingShopId}&limit=24`,
        { headers: authHeaders(), credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to load monthly statements");
      const data = await res.json();
      const list = data.statements || [];
      const monthNow = data.current_month || new Date().toISOString().slice(0, 7);
      setStatements(list);
      setUpcomingStatement(data.upcoming_statement || null);
      setCurrentMonth(monthNow);
      setHasAiVoice(Boolean(data.has_ai_voice));
      setSelectedMonth((prev) => {
        if (prev === data.upcoming_statement?.statement_month) return prev;
        if (prev && (list.some((s) => s.month === prev) || prev === monthNow)) return prev;
        return monthNow;
      });
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to load statements");
    } finally {
      setLoading(false);
    }
  }, [mechanicId, billingShopId]);

  const fetchInvoice = useCallback(async () => {
    if (!billingShopId || !selectedMonth) return;
    try {
      setInvoiceLoading(true);
      const apiBase = getApiBase();
      const url = isAccruingMonth(selectedMonth)
        ? `${apiBase}/api/mechanicProfile/monthly-statement-preview?mechanic_id=${mechanicId}&shop_id=${billingShopId}`
        : `${apiBase}/api/mechanicProfile/monthly-statement-detail?mechanic_id=${mechanicId}&shop_id=${billingShopId}&month=${selectedMonth}`;
      const res = await fetch(url, { headers: authHeaders(), credentials: "include" });
      if (!res.ok) throw new Error("Failed to load invoice detail");
      setInvoice(await res.json());
    } catch (err) {
      console.error(err);
      setInvoice(null);
    } finally {
      setInvoiceLoading(false);
    }
  }, [mechanicId, billingShopId, selectedMonth, isAccruingMonth]);

  useEffect(() => {
    if (open) fetchStatements();
  }, [open, fetchStatements]);

  useEffect(() => {
    if (invoiceOpen) fetchInvoice();
  }, [invoiceOpen, fetchInvoice]);

  const finalizedStatement =
    statements.find((s) => s.month === selectedMonth) || {
      month: selectedMonth,
      subscription_total: 0,
      platform_fee_total: 0,
      ai_voice_total: 0,
      statement_total: 0,
      job_count: 0,
    };

  const displayStatement = isAccruingMonth(selectedMonth)
    ? {
        month: upcomingStatement.statement_month,
        subscription_total: upcomingStatement.subscription_total || 0,
        platform_fee_total: upcomingStatement.platform_fee_total || 0,
        ai_voice_total: upcomingStatement.ai_voice_total || 0,
        statement_total: upcomingStatement.statement_total || 0,
        job_count: upcomingStatement.job_count || 0,
        platform_fee_accrual_month: upcomingStatement.accrual_month,
        ai_voice_accrual_month: upcomingStatement.accrual_month,
        ai_voice_usage_total: upcomingStatement.ai_voice_usage_total || 0,
      }
    : finalizedStatement;

  const pastStatements = statements.filter((s) => s.month !== currentMonth);

  const handlePrint = () => {
    const printRoot = document.getElementById("monthly-statement-print");
    if (!printRoot) return;
    const titleMonth = isAccruingMonth(selectedMonth)
      ? `${formatMonthLabel(selectedMonth)} (Accruing)`
      : formatMonthLabel(selectedMonth);
    const logoUrl = getInvoiceLogoUrl();
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;

    let html = printRoot.innerHTML;
    html = html.replace(/src="[^"]*UpdatedMainLogo\.png"/g, `src="${logoUrl}"`);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Statement — ${titleMonth}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #111; font-size: 12px; }
            .invoice-preview-note { color: #555; font-size: 11px; margin-bottom: 16px; font-style: italic; }
            .invoice-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 2px solid #e63926; }
            .invoice-logo { max-height: 52px; width: auto; display: block; }
            .invoice-page { page-break-after: always; }
            .invoice-page:last-child { page-break-after: auto; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            th, td { border: 1px solid #ccc; padding: 8px 10px; text-align: left; }
            th { background: #f5f5f5; }
          </style>
        </head>
        <body>${html}</body>
      </html>
    `);
    printWindow.document.close();

    const triggerPrint = () => {
      printWindow.focus();
      printWindow.print();
    };

    const imgs = printWindow.document.images;
    if (!imgs.length) {
      triggerPrint();
      return;
    }
    let loaded = 0;
    const onImageDone = () => {
      loaded += 1;
      if (loaded >= imgs.length) triggerPrint();
    };
    Array.from(imgs).forEach((img) => {
      if (img.complete) onImageDone();
      else {
        img.onload = onImageDone;
        img.onerror = onImageDone;
      }
    });
  };

  const scrollToDetail = (sectionKey) => {
    if (sectionKey === "ai_voice") {
      document.getElementById("invoice-detail-ai_voice")?.scrollIntoView({ behavior: "smooth" })
        || document.getElementById("invoice-detail-ai_voice_usage")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    document.getElementById(`invoice-detail-${sectionKey}`)?.scrollIntoView({ behavior: "smooth" });
  };

  if (!billingShopId && activeShops.length === 0) {
    return null;
  }

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<ReceiptLongIcon />}
        onClick={() => setOpen(true)}
        sx={{
          color: "#fff",
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "#1a73e8",
          borderColor: "#1a73e8",
          transition: "0.3s",
          "&:hover": {
            transform: "scale(1.03)",
            backgroundColor: "#1557b0",
            borderColor: "#1557b0",
          },
        }}
      >
        Monthly Statement
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold">
              Monthly Statement
            </Typography>
            <IconButton onClick={() => setOpen(false)} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              {activeShops.length > 1 && (
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel id="billing-shop-label">Billing location</InputLabel>
                  <Select
                    labelId="billing-shop-label"
                    value={billingShopId || ""}
                    label="Billing location"
                    onChange={(e) => setBillingShopId(Number(e.target.value))}
                  >
                    {activeShops.map((shop) => (
                      <MenuItem key={shop.id} value={shop.id}>
                        {formatShopLabel(shop)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="statement-month-label">Statement month</InputLabel>
                <Select
                  labelId="statement-month-label"
                  value={selectedMonth}
                  label="Statement month"
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {currentMonth && (
                    <MenuItem value={currentMonth}>
                      {formatMonthLabel(currentMonth)} (Current)
                    </MenuItem>
                  )}
                  {upcomingStatement?.statement_month && (
                    <MenuItem value={upcomingStatement.statement_month}>
                      {formatMonthLabel(upcomingStatement.statement_month)} (Accruing)
                    </MenuItem>
                  )}
                  {pastStatements.map((s) => (
                    <MenuItem key={s.month} value={s.month}>
                      {formatMonthLabel(s.month)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: "grey.50" }}>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>
                  Summary — {formatMonthLabel(selectedMonth)}
                  {selectedMonth === currentMonth && " (Current)"}
                  {isAccruingMonth(selectedMonth) && " (Accruing)"}
                </Typography>
                <Box sx={{ mt: 1.5 }}>
                  <Box display="flex" justifyContent="space-between" py={0.75}>
                    <Typography variant="body2">Subscriptions</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ${displayStatement.subscription_total.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" py={0.75}>
                    <Typography variant="body2">
                      Platform service fees
                      {displayStatement.platform_fee_accrual_month && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          (work from {formatMonthLabel(displayStatement.platform_fee_accrual_month)})
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ${displayStatement.platform_fee_total.toFixed(2)}
                    </Typography>
                  </Box>
                  {hasAiVoice && (
                    <Box display="flex" justifyContent="space-between" py={0.75}>
                      <Typography variant="body2">
                        AI voice answering
                        {displayStatement.ai_voice_accrual_month && displayStatement.ai_voice_usage_total > 0 && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            (calls from {formatMonthLabel(displayStatement.ai_voice_accrual_month)})
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ${Number(displayStatement.ai_voice_total || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" py={0.5}>
                    <Typography variant="body1" fontWeight="bold">
                      Statement total
                    </Typography>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      ${displayStatement.statement_total.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ mt: 2, textTransform: "none" }}
                  onClick={() => setInvoiceOpen(true)}
                >
                  View full invoice (summary + detail pages)
                </Button>
              </Paper>

              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Statement history
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Subscriptions
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Platform fees
                    </TableCell>
                    {hasAiVoice && (
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        AI voice
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statements.map((s) => (
                    <TableRow
                      key={s.month}
                      hover
                      selected={s.month === selectedMonth}
                      sx={{ cursor: "pointer" }}
                      onClick={() => setSelectedMonth(s.month)}
                    >
                      <TableCell>
                        {formatMonthLabel(s.month)}
                        {s.month === currentMonth && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            (Current)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">${s.subscription_total.toFixed(2)}</TableCell>
                      <TableCell align="right">${s.platform_fee_total.toFixed(2)}</TableCell>
                      {hasAiVoice && (
                        <TableCell align="right">${Number(s.ai_voice_total || 0).toFixed(2)}</TableCell>
                      )}
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        ${s.statement_total.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={invoiceOpen} onClose={() => setInvoiceOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Full invoice
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatMonthLabel(selectedMonth)}
                {selectedMonth === currentMonth && " (Current)"}
                {isAccruingMonth(selectedMonth) && " (Accruing)"}
                {" — summary + itemized detail pages"}
              </Typography>
            </Box>
            <Box>
              <IconButton onClick={handlePrint} aria-label="Print" title="Print / Save PDF">
                <PrintIcon />
              </IconButton>
              <IconButton onClick={() => setInvoiceOpen(false)} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "grey.100" }}>
          {invoiceLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : !invoice ? (
            <Alert severity="error">Could not load invoice.</Alert>
          ) : (
            <Box id="monthly-statement-print">
              {(invoice.is_preview || isAccruingMonth(selectedMonth)) && (
                <Typography className="invoice-preview-note" variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Accruing statement — totals update as activity completes this month and are not final until the period closes.
                </Typography>
              )}
              <Paper className="invoice-page" sx={{ p: 3, mb: 2 }}>
                <InvoiceLogoHeader
                  shop={invoice.shop}
                  invoice={invoice}
                  pageLabel={`Page 1 — Summary · ${formatMonthLabel(invoice.statement_month)}`}
                />
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Charge summary
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Segment</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Detail</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.summary?.segments?.map((seg) => (
                      <TableRow key={seg.key}>
                        <TableCell>{seg.label}</TableCell>
                        <TableCell>
                          {seg.detail_page ? (
                            <>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {seg.period_label || `${seg.line_count} line(s)`}
                              </Typography>
                              <Link
                                component="button"
                                variant="caption"
                                onClick={() => scrollToDetail(seg.key)}
                                sx={{ cursor: "pointer" }}
                              >
                                See itemized detail — Page {seg.detail_page}
                              </Link>
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No charges this period
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          ${Number(seg.total).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Typography className="grand-total" sx={{ textAlign: "right", mt: 2, fontWeight: 700, fontSize: 18 }}>
                  Grand total: ${Number(invoice.summary?.grand_total || 0).toFixed(2)}
                </Typography>
                <Typography className="footnote" variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                  {invoice.disclaimer}
                </Typography>
              </Paper>

              {invoice.detail_sections?.map((section) => (
                <Paper
                  key={section.key}
                  id={`invoice-detail-${section.key}`}
                  className="invoice-page"
                  sx={{ p: 3, mb: 2, pageBreakBefore: "always" }}
                >
                  <InvoiceLogoHeader
                    shop={invoice.shop}
                    invoice={invoice}
                    pageLabel={`Page ${section.page} — ${section.title}`}
                  />
                  <Typography className="section-title" variant="subtitle1" fontWeight="bold">
                    {section.title}
                  </Typography>
                  <Typography className="section-sub" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {section.subtitle}
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {section.columns.map((col) => (
                          <TableCell
                            key={col.id}
                            align={col.align === "right" ? "right" : "left"}
                            sx={{ fontWeight: 600 }}
                          >
                            {col.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {section.rows.map((row, idx) => (
                        <TableRow key={`${section.key}-${idx}`}>
                          {section.columns.map((col) => (
                            <TableCell key={col.id} align={col.align === "right" ? "right" : "left"}>
                              {formatCell(col, row[col.id])}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Typography className="subtotal-row" sx={{ textAlign: "right", mt: 2, fontWeight: 700 }}>
                    Segment total: ${Number(section.subtotal).toFixed(2)}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<PrintIcon />} onClick={handlePrint} disabled={!invoice}>
            Print / Save PDF
          </Button>
          <Button onClick={() => setInvoiceOpen(false)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShopMonthlyStatement;
